// src/App.js
import React, { Suspense, lazy, createContext, useContext, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Link, Navigate, useLocation, matchPath } from "react-router-dom";
import {
  Menu, Moon, Sun, LogIn, LogOut, House, CalendarPlus, Megaphone, ShoppingBag, Ticket, Settings as SettingsIcon,
  LayoutDashboard, ClipboardList, Music, Trophy, Sparkles, Wallet, UserPlus, UserCheck, KeyRound,
} from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "./components/ui/sheet";
import { Button } from "./components/ui/button";
import BookingPage from "./BookingPage";
import HomePage from "./HomePage";
import './styles.css';
import logo from "./apple-touch-icon.png";
import logoDark from "./apple-touch-icon-dark.png";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, doc, query, where } from "firebase/firestore";
import { getDoc, onSnapshot } from "./queryMonitor"; // timed reads (Billing → Performance)
import { initPushNotifications } from "./nativeServices";
import { initWebPush } from "./webPushService";
import { useClientEmail, useClientAccount } from "./clientIdentity";
import { useRegistrationForm, useRegistrationStatus, listNames } from "./registrationForm";
import { isFamilyUser, reconcileFamilySession } from "./familyLogin";

export const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Phone-sized screens: a top bar with a burger button that slides the full
// menu in from the left, instead of a horizontally scrolling tab bar.
function MobileNav({ navItems, footerLink }) {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const current = navItems.find(item => matchPath({ path: item.path, end: item.end ?? item.path === '/' }, pathname))
    || EXTRA_PAGE_TITLES.find(item => matchPath(item.path, pathname));

  // Close the menu whenever the page changes (e.g. back button).
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header className="mobile-header">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" className="h-10 gap-2 px-2 -ml-2 font-semibold" aria-label="Open menu">
            <Menu className="h-6 w-6" />
            Menu
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 gap-0">
          <div className="sidebar-logo pr-14">
            <img src={theme === 'dark' ? logoDark : logo} alt="Authentrix" />
          </div>
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">Go to another page</SheetDescription>
          <nav className="mobile-menu-nav">
            {navItems.map(item => item.disabled ? (
              <span key={item.path} className="nav-item-disabled" aria-disabled="true">
                <item.icon className="nav-icon" aria-hidden="true" />
                <span>
                  {item.label}
                  <span className="mobile-menu-hint">{item.disabledHint || "Enter your email on Home first"}</span>
                </span>
              </span>
            ) : (
              <NavLink key={item.path} to={item.path} end={item.end ?? item.path === '/'} className={({ isActive }) => [isActive && 'active', item.tone === 'success' && 'nav-item-success'].filter(Boolean).join(' ')} onClick={() => setOpen(false)}>
                <item.icon className="nav-icon" aria-hidden="true" />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="sidebar-footer">
            <Button variant="outline" className="w-full justify-start gap-2" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </Button>
            {footerLink && <FooterItem item={footerLink} onDone={() => setOpen(false)} />}
          </div>
        </SheetContent>
      </Sheet>
      <span className="mobile-header-title">{current?.label}</span>
      <img className="mobile-header-logo" src={theme === 'dark' ? logoDark : logo} alt="" />
    </header>
  );
}

function SidebarLayout({ children, navItems, footerLink }) {
  const { theme, toggleTheme } = useContext(ThemeContext);
  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src={theme === 'dark' ? logoDark : logo} alt="Authentrix" />
        </div>
        <nav className="sidebar-nav">
          {navItems.map(item => item.disabled ? (
            <span key={item.path} className="nav-item-disabled" aria-disabled="true" title={item.disabledHint || "Enter your email on Home first"}>
              <item.icon className="nav-icon" aria-hidden="true" />
              {item.label}
            </span>
          ) : (
            <NavLink key={item.path} to={item.path} end={item.end ?? item.path === '/'} className={({ isActive }) => [isActive && 'active', item.tone === 'success' && 'nav-item-success'].filter(Boolean).join(' ')}>
              <item.icon className="nav-icon" aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === 'dark' ? '☀ Light' : '◐ Dark'}
          </button>
          {footerLink && <FooterItem item={footerLink} />}
        </div>
      </aside>
      <MobileNav navItems={navItems} footerLink={footerLink} />
      <main className="main-content">{children}</main>
    </div>
  );
}

// Temporarily disabled for launch testing — flip back to true to re-enable
// the "must enter an email before using Book/Info/Merch/Settings" gate.
const REQUIRE_CLIENT_EMAIL = false;

const CLIENT_NAV = [
  { label: 'Home',      path: '/', end: true, icon: House },
  { label: 'Book',      path: '/book',           icon: CalendarPlus, requiresEmail: true },
  { label: 'Info',      path: '/info',           icon: Megaphone,    requiresEmail: true },
  { label: 'Merch',     path: '/merch',          icon: ShoppingBag,  requiresEmail: true },
  // Always gated (independent of REQUIRE_CLIENT_EMAIL): seat bookings are tied
  // to a studio account, so this stays greyed until Home matches one.
  { label: 'Show',      path: '/show',           icon: Ticket,       requiresAccount: true },
  { label: 'Settings',  path: '/client-settings', icon: SettingsIcon, requiresEmail: true },
];

// Titles for the mobile top bar on pages that aren't menu items.
const EXTRA_PAGE_TITLES = [
  { label: 'Register', path: '/register' },
];

// Tucked into the menu footer rather than on Home — parents rarely need it.
const TEACHER_LOGIN_LINK = { label: 'Teacher login', path: '/login', icon: LogIn };
// Teachers log out from the same spot, on every admin page.
const TEACHER_LOGOUT_ACTION = {
  label: 'Log out',
  icon: LogOut,
  onClick: async () => { await signOut(auth); window.location.href = '/'; },
};

// The small link or action under the theme toggle in the sidebar/menu footer.
function FooterItem({ item, onDone }) {
  const Icon = item.icon;
  if (item.onClick) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="footer-link h-auto font-normal hover:bg-transparent"
        onClick={() => { onDone?.(); item.onClick(); }}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />{item.label}
      </Button>
    );
  }
  return (
    <Link to={item.path} className="footer-link" onClick={onDone}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />{item.label}
    </Link>
  );
}

const BASE_ADMIN_NAV = [
  { label: 'Dashboard', path: '/dashboard',     icon: LayoutDashboard },
  { label: 'Bookings',  path: '/bookings',      icon: ClipboardList },
  { label: 'Sign-ups',  path: '/signups',       icon: UserPlus },
  { label: 'Logins',    path: '/logins',        icon: KeyRound },
  { label: 'Info',      path: '/announcements', icon: Megaphone },
  { label: 'Practice',  path: '/practice',      icon: Music },
  { label: 'Comps',     path: '/comps',         icon: Trophy },
  { label: 'Highlights', path: '/highlights',   icon: Sparkles },
  { label: 'Billing',   path: '/billing',       icon: Wallet },
  { label: 'Merch',     path: '/merch-admin',   icon: ShoppingBag },
  { label: 'Show',      path: '/show-admin',    icon: Ticket },
  { label: 'Settings',  path: '/settings',      icon: SettingsIcon },
];

function ClientLayout({ children }) {
  const email = useClientEmail();
  const account = useClientAccount();
  const registrationForm = useRegistrationForm();
  const registration = useRegistrationStatus(email, registrationForm?.year);
  const navItems = CLIENT_NAV.map(item => {
    if (item.requiresAccount && !account) {
      return { ...item, disabled: true, disabledHint: 'Enter your account email on Home first' };
    }
    return { ...item, disabled: REQUIRE_CLIENT_EMAIL && item.requiresEmail && !email };
  });
  // Families signed up for the coming year see it in green, with their
  // dancers; everyone else gets a Register link while registrations are open.
  const registerItem = registration
    ? { label: `Signed up: ${listNames(registration.dancers)}`, path: '/register', icon: UserCheck, tone: 'success' }
    : registrationForm?.open ? { label: 'Register', path: '/register', icon: UserPlus } : null;
  if (registerItem) navItems.splice(1, 0, registerItem);
  return <SidebarLayout navItems={navItems} footerLink={TEACHER_LOGIN_LINK}>{children}</SidebarLayout>;
}

// Blocks the client-facing booking/info/merch/settings pages until an email
// has been entered on Home — nav greying alone doesn't stop direct URL access.
function RequireClientEmail({ children }) {
  const email = useClientEmail();
  if (REQUIRE_CLIENT_EMAIL && !email) return <Navigate to="/" replace />;
  return children;
}

// Same idea for account-only pages (the show seat booking) — a greyed tab
// doesn't stop someone typing /show directly.
function RequireClientAccount({ children }) {
  const account = useClientAccount();
  if (!account) return <Navigate to="/" replace />;
  return children;
}

function AppLayout({ children }) {
  const [isBillingAdmin, setIsBillingAdmin] = useState(false);
  // A family logged in with a WhatsApp code is signed in, but never staff.
  const [isFamily, setIsFamily] = useState(() => isFamilyUser(auth.currentUser));

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setIsFamily(isFamilyUser(user));
      if (!user || isFamilyUser(user) || !user.email) { setIsBillingAdmin(false); return; }
      const snap = await getDoc(doc(db, "billingAdmins", user.email));
      setIsBillingAdmin(snap.exists());
    });
    return () => unsub();
  }, []);

  // Live count of sign-ups waiting for review, shown on the nav item.
  const [newSignups, setNewSignups] = useState(0);
  const [newLogins, setNewLogins] = useState(0);
  useEffect(() => {
    let unsubSignups = () => {};
    const unsub = onAuthStateChanged(auth, (user) => {
      unsubSignups();
      if (!user || isFamilyUser(user)) { setNewSignups(0); setNewLogins(0); return; }
      const u1 = onSnapshot(
        query(collection(db, "registrations"), where("status", "==", "new")),
        (snap) => setNewSignups(snap.size),
        () => setNewSignups(0)
      );
      // Families waiting for a WhatsApp login code.
      const u2 = onSnapshot(
        query(collection(db, "loginRequests"), where("status", "==", "pending")),
        (snap) => setNewLogins(snap.size),
        () => setNewLogins(0)
      );
      unsubSignups = () => { u1(); u2(); };
    });
    return () => { unsub(); unsubSignups(); };
  }, []);

  if (isFamily) return <Navigate to="/" replace />;

  const navItems = BASE_ADMIN_NAV
    .filter(item => item.path !== '/billing' || isBillingAdmin)
    .map(item => item.path === '/signups' && newSignups > 0 ? { ...item, label: `${item.label} (${newSignups})` } : item)
    .map(item => item.path === '/logins' && newLogins > 0 ? { ...item, label: `${item.label} (${newLogins})` } : item);

  return <SidebarLayout navItems={navItems} footerLink={TEACHER_LOGOUT_ACTION}>{children}</SidebarLayout>;
}

const LoginPage      = lazy(() => import("./LoginPage"));
const TeacherDashboard = lazy(() => import("./TeacherDashboard"));
const Settings       = lazy(() => import("./Settings"));
const PerformancePage = lazy(() => import("./PerformancePage"));
const ClientSettings = lazy(() => import("./ClientSettings"));
const StudentDashboard = lazy(() => import("./StudentDashboard"));
const MerchPage      = lazy(() => import("./MerchPage"));
const MerchAdmin     = lazy(() => import("./MerchAdmin"));
const BillingLayout  = lazy(() => import("./BillingLayout"));
const BillingPage    = lazy(() => import("./BillingPage"));
const BillingClassesPage = lazy(() => import("./BillingClassesPage"));
const AccountsPage   = lazy(() => import("./AccountsPage"));
const StatsPage      = lazy(() => import("./StatsPage"));
const BankPage       = lazy(() => import("./BankPage"));
const NextYearClassesPage = lazy(() => import("./NextYearClassesPage"));
const InfoPage       = lazy(() => import("./InfoPage"));
const AnnouncementsAdmin = lazy(() => import("./AnnouncementsAdmin"));
const PracticeAdmin      = lazy(() => import("./PracticeAdmin"));
const CompsAdmin         = lazy(() => import("./CompsAdmin"));
const HighlightsAdmin    = lazy(() => import("./HighlightsAdmin"));
const BookingsCalendar   = lazy(() => import("./BookingsCalendar"));
const ShowPage           = lazy(() => import("./ShowPage"));
const ShowAdmin          = lazy(() => import("./ShowAdmin"));
const RegisterPage       = lazy(() => import("./RegisterPage"));
const SignupsAdmin       = lazy(() => import("./SignupsAdmin"));
const LoginRequestsAdmin = lazy(() => import("./LoginRequestsAdmin"));

function AppRoutes() {
  return (
    <Suspense fallback={<div className="loading-screen"><div className="spinner" /><p>Loading…</p></div>}>
      <Routes>
        {/* Public login — no chrome */}
        <Route path="/login" element={<LoginPage />} />

        {/* Client-facing routes (no login required) */}
        <Route path="/"               element={<ClientLayout><HomePage /></ClientLayout>} />
        <Route path="/book"           element={<ClientLayout><RequireClientEmail><BookingPage /></RequireClientEmail></ClientLayout>} />
        <Route path="/register"       element={<ClientLayout><RegisterPage /></ClientLayout>} />
        <Route path="/info"           element={<ClientLayout><RequireClientEmail><InfoPage /></RequireClientEmail></ClientLayout>} />
        <Route path="/merch"          element={<ClientLayout><RequireClientEmail><MerchPage /></RequireClientEmail></ClientLayout>} />
        <Route path="/show"           element={<ClientLayout><RequireClientAccount><ShowPage /></RequireClientAccount></ClientLayout>} />
        <Route path="/client-settings" element={<ClientLayout><RequireClientEmail><ClientSettings /></RequireClientEmail></ClientLayout>} />

        {/* Admin routes */}
        <Route path="/bookings"   element={<AppLayout><BookingsCalendar /></AppLayout>} />
        <Route path="/dashboard"  element={<AppLayout><TeacherDashboard /></AppLayout>} />
        <Route path="/settings"   element={<AppLayout><Settings /></AppLayout>} />
        <Route path="/students"   element={<AppLayout><StudentDashboard /></AppLayout>} />
        <Route path="/merch-admin" element={<AppLayout><MerchAdmin /></AppLayout>} />
        <Route path="/announcements" element={<AppLayout><AnnouncementsAdmin /></AppLayout>} />
        <Route path="/practice"      element={<AppLayout><PracticeAdmin /></AppLayout>} />
        <Route path="/comps"         element={<AppLayout><CompsAdmin /></AppLayout>} />
        <Route path="/highlights"    element={<AppLayout><HighlightsAdmin /></AppLayout>} />
        <Route path="/show-admin"    element={<AppLayout><ShowAdmin /></AppLayout>} />
        <Route path="/signups"       element={<AppLayout><SignupsAdmin /></AppLayout>} />
        <Route path="/logins"        element={<AppLayout><LoginRequestsAdmin /></AppLayout>} />
        <Route path="/billing" element={<AppLayout><BillingLayout /></AppLayout>}>
          <Route index element={<BillingPage />} />
          <Route path="classes" element={<BillingClassesPage />} />
          <Route path="next-year" element={<NextYearClassesPage />} />
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="bank" element={<BankPage />} />
          <Route path="stats" element={<StatsPage />} />
          <Route path="performance" element={<PerformancePage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function PushNotificationManager() {
  useEffect(() => {
    // Native push for admins logged in via Firebase Auth
    const unsub = onAuthStateChanged(auth, user => {
      if (user) initPushNotifications(user.uid);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    // Web push for clients identified by localStorage email
    initWebPush();
  }, []);

  return null;
}

// Holds the pages back until the startup login check has run, so no page
// starts out with a family that should have been asked to log in again.
function FamilySessionGate({ children }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      unsub(); // the first answer is the saved login (or none); that's all we need
      try { await reconcileFamilySession(user); }
      catch (err) { console.error("Login check failed:", err); }
      setReady(true);
    });
  }, []);
  if (!ready) return <div className="loading-screen"><div className="spinner" /><p>Loading…</p></div>;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <PushNotificationManager />
        <FamilySessionGate>
          <AppRoutes />
        </FamilySessionGate>
      </Router>
    </ThemeProvider>
  );
}

export default App;
